/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { getDateAfterSeconds } from '@airasia-phoenix/react-native/utils/dateUtils';
import { Easing } from 'react-native-reanimated';

// ------------------- BAGGAGE -------------------

export const HANDBAG_CODES = ['CBWT', 'APVP', 'IMMG', 'COMP_CBWT'];
export const BAGGAGE_KEYWORDS = [
  'bag',
  'cbwt',
  'checked',
  'carry',
  'hold',
  'hand',
];
export const BAGGAGE_TYPES = [
  'baggageItems',
  'checked_bag',
  'cabin_bag',
  'hold',
  'hand',
  'baggage',
  'sports_equipment',
];

export const BAGGAGE_CATEGORY = {
  HOLD: 'hold',
  HAND: 'hand',
  SPORTS_EQUIPMENT: 'sportsEquipment',
} as const;

export const BAGGAGE_DIMENSION_CODE = {
  PERSONAL_ITEM: 'personal_item',
  CABIN_BAG: 'cabin_bag',
  HAND_BAG: 'hand_bag',
  HOLD_BAG: 'hold_bag',
  CHECK_BAG: 'check_bag',
  SPORTS_EQUIP: 'sports_equip',
  SPORTS_EQUIPMENT: 'sports_equipment',
} as const;

export const BAGGAGE_SUMMARY_FALLBACK_LABEL = {
  PIECE: 'piece',
  PIECES: 'pieces',
  NO_BAGGAGE_SELECTED: 'No baggage selected',
} as const;

export const CARRY_ON_BAGGAGE_REGEX = /hand|cabin|cbwt/i;
export const CHECKED_BAGGAGE_REGEX = /hold|checked/i;

// ------------------- COMPANY DETAILS -------------------

export const COMPANY_DETAILS_TRACKING_LABELS = {
  expand: 'company details|expand',
  collapse: 'company details|collapse',
  info: 'company details|info',
  fieldRequired: 'company details|field required',
  missingDetails: 'company details|missingdetails',
} as const;
export type CompanyDetailsTrackingKey =
  keyof typeof COMPANY_DETAILS_TRACKING_LABELS;
export type CompanyDetailsTrackingValue =
  (typeof COMPANY_DETAILS_TRACKING_LABELS)[CompanyDetailsTrackingKey];

// ------------------- FILTERS & SORT -------------------

export const defaultSortOptions = [
  'recommended',
  'best',
  'cheapest',
  'fastest',
] as const;
export type SortOption = (typeof defaultSortOptions)[number];

export const defaultSort = 'best';
export const RECOMMENDED_SORT: SortOption = 'recommended';

export const FILTER_DURATIONS = {
  maxJourneyDuration: 59,
  minJourneyDuration: 0,
  maxStopoverDuration: 25,
  minStopoverDuration: 0,
} as const;

export const POPULAR_FILTER = {
  AIR_ASIA_FLIGHTS_ONLY: 'AIR_ASIA_FLIGHTS_ONLY',
  DIRECT_FLIGHTS: 'DIRECT_FLIGHTS',
  CHECKED_BAGGAGE_INCLUDED: 'CHECKED_BAGGAGE_INCLUDED',
} as const;
export type PopularFilterType =
  (typeof POPULAR_FILTER)[keyof typeof POPULAR_FILTER];

// ------------------- SESSION -------------------

export const sessionTimerConstants = {
  sessionTime: 899,
  timeLeft: 60,
} as const;

// ------------------- NOTIFICATION -------------------
export const NOTIFICATION_TYPE = {
  ALERT: 0,
  INFO: 1,
  SUCCESS: 2,
} as const;
export type NotificationType = keyof typeof NOTIFICATION_TYPE;

export const NOTIFICATION_VIEW_STYLE_KEYS = {
  success: 'SUCCESS',
  warning: 'WARNING',
  error: 'ERROR',
  info: 'INFO',
} as const;

export const DOC_TYPES: Record<string, string> = {
  passport: 'Passport',
  id: 'National ID',
  mykad: 'MyKad',
  mal: 'MyKad',
  mac: 'MyKad',
} as const;

// Fallback expiry date for national ID doc to avoid Fav Member API failures,
// Identifier used to persist travel document type.
export const ID_EXP_DATE = '31122199' as const;
export const FAV_MEMBERS_LIMIT_ERROR_CODE = 'FAVORITE_MEMBERS_NUMBER_EXCEED';

// ------------------- POINTS / CURRENCY -------------------
export const POINTS_CURRENCY = ['LPX', 'PTS'] as const;
export type PointsCurrency = (typeof POINTS_CURRENCY)[number];

// ------------------- SEARCH QUERY -------------------
export const SEARCH_QUERY_PARAMS = {
  PAGE: 'page',
  AIRLINE_PROFILE: 'airlineProfile',
  TYPE: 'type',
} as const;

// ------------------- REMOTE CONFIGS -------------------
export const NATIVE_REMOTE_CONFIGS = {
  FLIGHTS_UNIFIED_CHECKOUT: 'flights_unified_checkout_enabled',
  FLIGHTS_CHANGE_PAX_TYPE_COUNTRIES: 'flights_change_pax_type_countries',
} as const;

export const FeatureFlags = {
  FLIGHTS_FEEDBACK_WIDGET: 'flights_feedback_enabled',
  FLIGHTS_POINTS_PLUS_CASH_ENABLED: 'flights_points_n_cash_enabled',
} as const;

// ------------------- A/B EXPERIMENTS -------------------
export const A_B_EXPERIMENTS = {
  ANCILLARY: 'ancillary',
  SEARCH_RECOMMENDATION: 'searchRecommendation',
  SNAP_RECOMMENDATION: 'snapUpsell',
  INCREMENTAL_SEARCH: 'incrementalSearch',
} as const;
export type ABExperimentKey = keyof typeof A_B_EXPERIMENTS;

// ------------------- CHECKOUT API -------------------
export const CHECKOUT_API_VERSION = {
  V4: 'v4',
  V3: 'v3',
  V1: 'v1',
} as const;
export type CheckoutApiVersion =
  (typeof CHECKOUT_API_VERSION)[keyof typeof CHECKOUT_API_VERSION];

// ------------------- AIRLINE PROFILES -------------------
export const AIRLINE_PROFILE = {
  DOTREZ: 'dotrez',
  KIWI: 'kiwi',
  GOQUO: 'goquo',
  VIETJET: 'vietjet',
} as const;
export type AirlineProfileKey = keyof typeof AIRLINE_PROFILE;
export type AirlineProfileValue = (typeof AIRLINE_PROFILE)[AirlineProfileKey];

export const INCLUDEANCILLARY: Record<string, string[]> = {
  KIWI: ['baggage', 'insurance'],
  DOTREZ: [
    'baggage',
    'bundle',
    'meal',
    'insurance',
    'fastPass',
    'immigrationFastLane',
    'carbonOffset',
    'autoApplySSR',
  ],
  GOQUO: ['baggage', 'insurance'],
  VIETJET: ['baggage', 'insurance'],
};

export const RECOMMENDATION_ANCILLARIES = ['baggage', 'bundle', 'meal'];

export const AIRASIA_CODES = [
  'AK',
  'D7',
  'FD',
  'QZ',
  'XJ',
  'Z2',
  'KT',
  'I5',
  'PC',
] as const;
export type AirAsiaCode = (typeof AIRASIA_CODES)[number];

// ------------------- CABIN CLASS -------------------
export const CABIN_CLASS = {
  ECONOMY: 'ECONOMY',
  PREMIUM_ECONOMY: 'PREMIUM_ECONOMY',
  BUSINESS: 'BUSINESS',
  FIRST_CLASS: 'FIRST_CLASS',
  FIRST: 'FIRST',
} as const;
export type CabinClassType = (typeof CABIN_CLASS)[keyof typeof CABIN_CLASS];

export const FARE_CLASS_CATEGORY = {
  CATEGORY_M: 'M',
  CATEGORY_C: 'C',
  CATEGORY_W: 'W',
  CATEGORY_F: 'F',
};
export type FareClassCategoryType =
  (typeof FARE_CLASS_CATEGORY)[keyof typeof FARE_CLASS_CATEGORY];

// ------------------- BUNDLE -------------------
export const BUNDLE_NAME = {
  BASIC: 'Economy',
  VALUE_PACK: 'Value Pack',
  PREMIUM_FLEX: 'Premium Flex',
  PREMIUM_FLATBED: 'Premium Flatbed',
} as const;
export type BundleName = (typeof BUNDLE_NAME)[keyof typeof BUNDLE_NAME];

export const BUNDLE_ID = {
  BASIC: 'EC',
  VALUE_PACK: 'VP',
  PREMIUM_FLEX: 'PF',
  PREMIUM_FLATBED: 'PM',
} as const;
export type BundleId = (typeof BUNDLE_ID)[keyof typeof BUNDLE_ID];

export const BUNDLE_PROPERTIES = {
  [BUNDLE_ID.BASIC]: {
    bundleCode: 'BASIC',
    bundleName: 'Economy',
  },
  [BUNDLE_ID.VALUE_PACK]: {
    bundleCode: 'VP',
    bundleName: 'Value Pack',
  },
  [BUNDLE_ID.PREMIUM_FLEX]: {
    bundleCode: 'PF',
    bundleName: 'Premium Flex',
  },
  [BUNDLE_ID.PREMIUM_FLATBED]: {
    bundleCode: 'PM',
    bundleName: 'Premium Flatbed',
  },
} as const;

// ------------------- GOOGLE ADS -------------------
export const GOOGLE_AD_TYPE = {
  NATIVE: 'native',
  PRIME: 'prime',
} as const;
export type GoogleAdType = (typeof GOOGLE_AD_TYPE)[keyof typeof GOOGLE_AD_TYPE];

export const LFC_CATEGORY_NAME = 'hpsearchwidget|datepicker' as const;

export const JOURNEY_TYPE = {
  RETURN: 'Return',
  ONE_WAY: 'One Way',
} as const;

export type JourneyType = (typeof JOURNEY_TYPE)[keyof typeof JOURNEY_TYPE];

export const JOURNEY_MODES = {
  AIRPORT: 'AIRPORT',
  BUS: 'BUS',
  FERRY: 'FERRY',
} as const;

export type JourneyMode = (typeof JOURNEY_MODES)[keyof typeof JOURNEY_MODES];

export const STATIONS = {
  TYPE: {
    AIRPORT: 'A',
    CITY: 'C',
    FERRY: 'F',
    BUS: 'B',
  },
  DEFAULT_LAT_LONG: '0,0',
} as const;

export const AdditionalLabels = {
  ALL_AIRPORTS_DISPLAY_STRING: ' ({0})',
  WEEK_DAYS: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  PRICE_STRING: '{0} {1}',
  PASSENGER_TEXT_FIELD_DISPLAY_STRING_FORMAT: '{0} {1}, {2}',
};

export const JOURNEY_DATE_CONFIG = {
  get departDate() {
    //default is 7 days from today
    const timeInDays = 7;
    const timeInHours = timeInDays * 24;
    const timeInMins = timeInHours * 60;
    const timeInSeconds = timeInMins * 60;
    return getDateAfterSeconds(new Date(), timeInSeconds);
  },
  get returnDate() {
    //default is 10 days from today, (3 days from departDate)
    const timeInDays = 10;
    const timeInHours = timeInDays * 24;
    const timeInMins = timeInHours * 60;
    const timeInSeconds = timeInMins * 60;
    return getDateAfterSeconds(new Date(), timeInSeconds);
  },
};

// ------------------- TRAVEL DOCUMENTS -------------------
export const ALL = 'All';

export const STOP_TYPE = {
  ANY: 'ANY',
  NON_STOP: 'NS',
  UPTO_1: 'UPTO_1',
  UPTO_2: 'UPTO_2',
} as const;
export type StopType = (typeof STOP_TYPE)[keyof typeof STOP_TYPE];

export const FLIGHT_LIST_ROW_TYPE = {
  HEADER: 'HEADER',
  STICKY_DESC: 'STICKY_DESC',
  SKELETON: 'SKELETON',
  ERROR: 'ERROR',
  CROSS_SELL: 'CROSS_SELL',
  FLIGHT: 'FLIGHT',
} as const;

export enum TripFlowType {
  PAIRED = 'paired',
  BUNDLED = 'bundled',
}

export type TripFlowTypeValue = TripFlowType;

export enum SegmentType {
  DEPARTURE = 'departure',
  RETURN = 'return',
}

export enum PaxType {
  ADULT = 'adult',
  CHILD = 'child',
  INFANT = 'infant',
}

export enum PaxTypeShort {
  ADULT = 'adt',
  CHILD = 'chd',
  INFANT = 'inf',
  INFANTF = 'inft',
}

export enum PaxTypeWithYPTA {
  REAL_ADULT = 'real_adult',
  ADULT_12TO16 = 'ypta_12_16',
  ADULT_16TO18 = 'ypta_16_18',
  CHILD = 'child',
  INFANT = 'infant',
}

export const ZERO_FARE_CODE = 'CHG1';

export const PROVIDERS = {
  AAB: 'AAB',
  OTA: 'OTA',
} as const;

export const CROSS_SELL_TYPE = {
  FLIGHT_BUS: 'Flight_Bus_Banner',
  FLIGHT_HOTEL: 'Snap_Banner',
  FLATBED: 'Flatbed_Banner',
} as const;

export const BannerInfo = {
  STATUS: 'active',
  TYPE: 'Banner',
} as const;

export const UpSell_Type = {
  snap: 'upsellSnap',
  flightBus: 'upsellFlightBus',
  flatbed: 'upsellFlatbed',
  premiumFlatBed: 'upsellPremiumFlatBed',
} as const;

export const HIDE_PULL_SPINNER = false;

export const DEFAULT_LOCALE = 'en-gb';

export const TRIP_TYPE = {
  RETURN: 'R',
  ONE_WAY: 'O',
} as const;

export enum FareType {
  BASE_FARE = 'baseFare',
  TAXES = 'taxes',
  ADD_ONS = 'addOns',
  OTHER_SERVICES = 'otherServices',
  DISCOUNT = 'discount',
  SPECIAL_ASSISTANCE = 'specialAssistance',
}

export const DEFAULT_CURRENCY = 'MYR';

// ------------------- CARBON OFFSET -------------------
export const CARBON_OFFSET_IDS = {
  PERCENT_100: 'XST1',
  PERCENT_50: 'XST2',
} as const;

export const DEFAULT_CHANGE_PAX_TYPE_COUNTRIES = ['MV', 'NP', 'KH'];

export const PAX_LABEL = {
  adult: 'Adult',
  child: 'Child',
  infant: 'Infant',
} as const;

export const NO_INSURANCE_SSR_FEE_CODE = 'NOINS'; // NO INSURANCE SSR CODE
export const FDI_SSR_FEE_CODES = new Set(['INS', 'INMF', 'FDGM']);

export const FAQ_URL = 'https://www.airasia.com/unlimited/en/gb/faq';
export const TNC_URL =
  'https://www.airasia.com/aa/about-us/en/gb/terms-and-conditions.html';
export const POLICY_SEARCH_URL =
  'https://www.tuneprotect.com/airasia/policy-search/';

// ------------------- EMAIL DOMAINS -------------------
export const EMAIL_DOMAIN_LIST = [
  '@gmail.com',
  '@yahoo.com',
  '@hotmail.com',
  '@outlook.com',
  '@icloud.com',
  '@airasia.com',
  '@rediffmail.com',
];

export const MY_COUNTRY_CODE = 'MY';
export const MALAYSIA_COUNTRY = 'malaysia';
export const MYKAD = ['mal', 'mac'];

export const customEasing = Easing.bezier(0.0, 0.0, 0.2, 1);
export const ANIMATION_DURATION = 300; // 300ms
export const CHECKOUT_TYPE = {
  flightUnavailable: 'flightUnavailable',
  mealsSoldOut: 'mealsSoldOut',
  fastpassSoldOut: 'fastpassSoldOut',
  wheelchairSoldOut: 'wheelchairSoldOut',
  seatSoldOut: 'seatSoldOut',
  passedCutoffTime: 'passedCutoffTime',
  passedCutoffTimeSeat: 'passedCutoffTimeSeat',
  passedCutoffTimeBaggage: 'passedCutoffTimeBaggage',
  passedCutoffTimeMeal: 'passedCutoffTimeMeal',
  passedCutoffTimeWheelchair: 'passedCutoffTimeWheelchair',
};
